import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { api } from '../services/api';
import { 
  ArrowLeft, 
  Users, 
  Target, 
  Briefcase,
  TrendingUp,
  Star,
  Plus
} from 'lucide-react';

const SkillDetails = () => {
  const { id } = useParams();
  const { isDarkMode } = useTheme();
  const [activeTab, setActiveTab] = useState('overview');

  const [allSkills, setAllSkills] = useState(() => JSON.parse(localStorage.getItem('skillmesh_skills') || '[]'));
  const [allPeople, setAllPeople] = useState(() => JSON.parse(localStorage.getItem('skillmesh_people') || '[]'));
  const [allProjects, setAllProjects] = useState(() => JSON.parse(localStorage.getItem('skillmesh_projects') || '[]'));

  useEffect(() => {
    api.getSkills().then(data => { if (Array.isArray(data)) setAllSkills(data); });
    api.getPeople().then(data => { if (Array.isArray(data)) setAllPeople(data); });
    api.getProjects().then(data => { if (Array.isArray(data)) setAllProjects(data); });

    const handleStorageChange = () => {
      setAllSkills(JSON.parse(localStorage.getItem('skillmesh_skills') || '[]'));
      setAllPeople(JSON.parse(localStorage.getItem('skillmesh_people') || '[]'));
      setAllProjects(JSON.parse(localStorage.getItem('skillmesh_projects') || '[]'));
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Match current skill by ID or name
  const currentSkill = allSkills.find(s => 
    String(s.id) === String(id) || 
    (s.name && s.name.toLowerCase() === decodeURIComponent(id || '').toLowerCase())
  ) || {
    id: id,
    name: decodeURIComponent(id || 'Skill').replace(/^skill_/, '').replace(/_/g, ' '),
    category: 'General',
    description: `Expertise and practical project experience with ${decodeURIComponent(id || 'this skill')}.`
  };

  const skillNameLower = (currentSkill.name || '').toLowerCase();

  // Find real people who actually possess this skill
  const matchingPeople = allPeople.filter(p => {
    const pSkills = (p.skills || []).map(s => (typeof s === 'string' ? s : s?.name || '').toLowerCase());
    return pSkills.includes(skillNameLower);
  }).map(p => {
    const initials = p.name ? p.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() : '??';
    const yearsMatch = String(p.experience || '').match(/\d+/);
    const years = yearsMatch ? parseInt(yearsMatch[0], 10) : 3;
    return {
      id: p.id,
      name: p.name,
      avatar: initials,
      level: years >= 6 ? 'Expert' : (years >= 3 ? 'Advanced' : 'Intermediate'),
      years: years,
      rating: p.rating || 5.0,
      projects: (p.projects || []).length
    };
  });

  // Find real projects that actually require this skill
  const matchingProjects = allProjects.filter(pr => {
    const req = (pr.requiredSkills || []).map(s => (typeof s === 'string' ? s : s?.name || '').toLowerCase());
    return req.includes(skillNameLower);
  }).map(pr => ({
    id: pr.id,
    name: pr.name,
    status: pr.status || 'Planning',
    importance: 'High',
    teamSize: pr.teamSize || 3
  }));

  const peopleCount = matchingPeople.length;
  const projectDemand = matchingProjects.length;
  const gap = projectDemand - peopleCount;
  const avgRating = matchingPeople.length > 0 
    ? (matchingPeople.reduce((acc, p) => acc + (p.rating || 5), 0) / matchingPeople.length).toFixed(1)
    : '5.0';

  const skill = {
    ...currentSkill,
    peopleCount,
    projectDemand,
    gap,
    trending: 'up',
    averageRating: avgRating,
    people: matchingPeople,
    projects: matchingProjects,
    relatedSkills: currentSkill.relatedSkills || ['Best Practices', 'Agile Delivery', 'Problem Solving'],
    learningPath: currentSkill.learningPath || [
      `${currentSkill.name} Fundamentals`,
      `Applied Workflows & Tooling`,
      `Production Implementations`,
      `Advanced Architecture & Optimization`
    ]
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: TrendingUp },
    { id: 'people', label: 'People', icon: Users },
    { id: 'projects', label: 'Projects', icon: Briefcase },
    { id: 'learning', label: 'Learning', icon: Target }
  ];

  const getLevelColor = (level) => {
    switch (level) {
      case 'Expert': return isDarkMode ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30' : 'bg-purple-50 text-purple-800 border border-purple-300 font-semibold';
      case 'Advanced': return isDarkMode ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' : 'bg-blue-50 text-blue-800 border border-blue-300 font-semibold';
      case 'Intermediate': return isDarkMode ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30' : 'bg-amber-100 text-amber-950 border border-amber-300 font-bold';
      case 'Beginner': return isDarkMode ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-green-50 text-green-800 border border-green-300 font-semibold';
      default: return isDarkMode ? 'bg-dark-700 text-dark-300' : 'bg-light-200 text-light-700';
    }
  };

  const getImportanceColor = (importance) => {
    switch (importance) {
      case 'Critical': return isDarkMode ? 'bg-red-500/20 text-red-400 border border-red-500/30' : 'bg-red-50 text-red-800 border border-red-300 font-semibold';
      case 'High': return isDarkMode ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30' : 'bg-orange-50 text-orange-800 border border-orange-300 font-semibold';
      case 'Medium': return isDarkMode ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30' : 'bg-amber-100 text-amber-950 border border-amber-300 font-bold';
      case 'Low': return isDarkMode ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-green-50 text-green-800 border border-green-300 font-semibold';
      default: return isDarkMode ? 'bg-dark-700 text-dark-300' : 'bg-light-200 text-light-700';
    }
  };

  const getProjectStatusColor = (status) => {
    switch (status) {
      case 'Completed': return isDarkMode ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-green-50 text-green-800 border border-green-300 font-semibold';
      case 'In Progress': return isDarkMode ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' : 'bg-blue-50 text-blue-800 border border-blue-300 font-semibold';
      case 'Planning': return isDarkMode ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30' : 'bg-amber-100 text-amber-950 border border-amber-300 font-semibold';
      default: return isDarkMode ? 'bg-dark-700 text-dark-300' : 'bg-light-200 text-light-700';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Link
          to="/skills"
          className="p-2 rounded-lg bg-dark-800 hover:bg-dark-700 transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div className="flex-1">
          <h1 className="text-3xl font-bold">{skill.name}</h1>
          <p className="text-dark-300">{skill.category} • {skill.description.split('.')[0]}</p>
        </div>
        <button className="btn-primary">
          <Plus className="h-5 w-5 mr-2" />
          Add Person
        </button>
      </div>

      {/* Skill Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card text-center">
          <div className="text-2xl font-bold text-purple-400">{skill.peopleCount}</div>
          <div className="text-sm text-dark-300">People</div>
        </div>
        
        <div className="card text-center">
          <div className="text-2xl font-bold text-blue-400">{skill.projectDemand}</div>
          <div className="text-sm text-dark-300">Project Demand</div>
        </div>
        
        <div className="card text-center">
          <div className={`text-2xl font-bold ${skill.gap > 0 ? 'text-red-400' : 'text-green-400'}`}>
            {skill.gap > 0 ? `Gap: ${skill.gap}` : `Surplus: ${Math.abs(skill.gap)}`}
          </div>
          <div className="text-sm text-dark-300">Status</div>
        </div>
        
        <div className="card text-center">
          <div className="text-2xl font-bold text-yellow-400">{skill.averageRating}</div>
          <div className="text-sm text-dark-300">Avg Rating</div>
        </div>
      </div>

      {/* Gap Analysis */}
      <div className="card">
        <h3 className="text-lg font-semibold mb-4">Skills Gap Analysis</h3>
        <div className="space-y-4">
          <div>
            <div className="flex justify-between text-sm mb-2">
              <span className="text-dark-300">Supply vs Demand</span>
              <span className={skill.gap > 0 ? 'text-red-400' : 'text-green-400'}>
                {skill.gap > 0 ? 'High Demand' : 'High Supply'}
              </span>
            </div>
            <div className="w-full bg-dark-800 rounded-full h-3 overflow-hidden">
              <div className="flex h-full">
                <div 
                  className="bg-purple-500 transition-all duration-300"
                  style={{ width: `${(skill.peopleCount / Math.max(skill.peopleCount, skill.projectDemand)) * 100}%` }}
                />
                <div 
                  className="bg-blue-500 transition-all duration-300"
                  style={{ width: `${(skill.projectDemand / Math.max(skill.peopleCount, skill.projectDemand)) * 100}%` }}
                />
              </div>
            </div>
            <div className="flex justify-between text-xs mt-1">
              <span className="text-purple-400">Supply ({skill.peopleCount})</span>
              <span className="text-blue-400">Demand ({skill.projectDemand})</span>
            </div>
          </div>
          
          <div className="p-4 bg-dark-800 rounded-lg">
            <p className="text-sm text-dark-300">
              {skill.gap > 0 
                ? `This skill has a gap of ${skill.gap} people. Consider upskilling team members or hiring new talent.`
                : `This skill has a surplus of ${Math.abs(skill.gap)} people. The team is well-covered for this skill.`
              }
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="card">
        <div className="border-b border-dark-800">
          <nav className="flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-400'
                    : 'border-transparent text-dark-300 hover:text-white hover:border-dark-700'
                }`}
              >
                <tab.icon className="h-4 w-4" />
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="py-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-3">Description</h3>
                <p className="text-dark-300 leading-relaxed">{skill.description}</p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold mb-3">Skill Information</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-dark-300">Category:</span>
                      <span>{skill.category}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-dark-300">Trending:</span>
                      <span className="text-green-400">↗ Up</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-dark-300">Average Rating:</span>
                      <div className="flex items-center space-x-1">
                        <Star className="h-4 w-4 text-yellow-400 fill-current" />
                        <span>{skill.averageRating}/5</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold mb-3">Related Skills</h3>
                  <div className="flex flex-wrap gap-2">
                    {skill.relatedSkills.map((relatedSkill) => (
                      <span key={relatedSkill} className="px-3 py-2 bg-dark-800 rounded-lg text-sm">
                        {relatedSkill}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'people' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">People with {skill.name}</h3>
                <span className="text-sm text-dark-400">Total: {skill.people.length}</span>
              </div>
              
              {skill.people.length === 0 ? (
                <div className="card text-center py-12 border border-dashed border-gray-700/50">
                  <Users className="h-10 w-10 text-gray-500 mx-auto mb-3" />
                  <h4 className="text-lg font-semibold mb-1">No Participants with this Skill Yet</h4>
                  <p className="text-dark-300 text-sm max-w-md mx-auto">
                    None of the active team members have listed "{skill.name}" in their defined skills.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {skill.people.map((person) => (
                    <div key={person.id} className="card">
                      <div className="flex items-start space-x-4">
                        <div className="w-12 h-12 bg-gradient-primary rounded-full flex items-center justify-center text-white font-semibold">
                          {person.avatar}
                        </div>
                        
                        <div className="flex-1">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <h4 className="font-semibold">{person.name}</h4>
                              <p className="text-sm text-dark-300">{person.years} years experience</p>
                            </div>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getLevelColor(person.level)}`}>
                              {person.level}
                            </span>
                          </div>
                          
                          <div className="flex items-center space-x-4 text-sm mb-3">
                            <div className="flex items-center space-x-1">
                              <Star className="h-4 w-4 text-yellow-400 fill-current" />
                              <span>{person.rating}</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <Briefcase className="h-4 w-4 text-blue-400" />
                              <span>{person.projects} projects</span>
                            </div>
                          </div>
                          
                          <div className="flex items-center space-x-3">
                            <Link 
                              to={`/people/${person.id}`}
                              className="text-primary-400 hover:text-primary-300 transition-colors text-sm"
                            >
                              View Profile →
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'projects' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Projects Requiring {skill.name}</h3>
                <span className="text-sm text-dark-400">Total: {skill.projects.length}</span>
              </div>
              
              {skill.projects.length === 0 ? (
                <div className="card text-center py-12 border border-dashed border-gray-700/50">
                  <Briefcase className="h-10 w-10 text-gray-500 mx-auto mb-3" />
                  <h4 className="text-lg font-semibold mb-1">No Projects Requiring this Skill</h4>
                  <p className="text-dark-300 text-sm max-w-md mx-auto">
                    There are currently no active projects demanding "{skill.name}".
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {skill.projects.map((project) => (
                    <div key={project.id} className="card">
                      <div className="flex items-start justify-between mb-3">
                        <h4 className="text-lg font-semibold">{project.name}</h4>
                        <div className="flex items-center space-x-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getProjectStatusColor(project.status)}`}>
                            {project.status}
                          </span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getImportanceColor(project.importance)}`}>
                            {project.importance}
                          </span>
                        </div>
                      </div>
                      
                      <div className="space-y-3">
                        <div className="flex justify-between text-sm">
                          <span className="text-dark-300">Team Size:</span>
                          <span>{project.teamSize} members</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-dark-300">Importance:</span>
                          <span className={getImportanceColor(project.importance)}>
                            {project.importance}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'learning' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold">Learning Path for {skill.name}</h3>
              
              <div className="space-y-4">
                {skill.learningPath.map((step, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-gradient-primary rounded-full flex items-center justify-center text-white font-semibold text-sm">
                      {index + 1}
                    </div>
                    
                    <div className="flex-1">
                      <h4 className="font-semibold mb-2">{step}</h4>
                      <div className="flex items-center space-x-4 text-sm text-dark-300">
                        <span>Estimated time: {index === 0 ? '2-4 weeks' : index === 1 ? '1-2 weeks' : '2-3 weeks'}</span>
                        <span>Difficulty: {index === 0 ? 'Beginner' : index === 1 ? 'Beginner' : 'Intermediate'}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="card bg-gradient-primary/10 border-primary-500/20">
                <h4 className="text-lg font-semibold mb-3">Ready to learn {skill.name}?</h4>
                <p className="text-dark-300 mb-4">
                  Start your learning journey with our recommended resources and track your progress.
                </p>
                <button className="btn-primary">
                  Start Learning Path
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SkillDetails;
